import React, { useEffect, useRef, useState } from "react";
import Layout from "../../components/layout";
import { FaArrowLeft } from "react-icons/fa";

import styles from "./OrderDetailsPage.module.scss";
import { Link, useNavigate, useParams } from "react-router-dom";
import { route } from "../../static/route";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchOrderDetailsData,
  getOrderDetails,
} from "../../features/orderDetails/orderDetailsSlice";
import { FaFilePdf, FaPlus } from "react-icons/fa6";
import OrderCloseForm from "../../components/orderDetails/OrderCloseForm";
import OrderPdf from "../../components/orderDetails/OrderPdf";
import ReceiveHistoryList from "../../components/orderDetails/ReceiveHistoryList";
import { useReactToPrint } from "react-to-print";

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const printRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const order = useAppSelector(getOrderDetails);

  const [isOrderCloseOpen, setIsOrderCloseOpen] = useState<boolean>(false);

  const handleGoToBack = () => {
    navigate(-1);
  };

  const handlePrintOrderData = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Order PDF - " + (order?.id || ""),
    onBeforeGetContent: () => {
      return new Promise<void>((resolve) => {
        setTimeout(resolve, 100);
      });
    },
  });

  const handleCloseOrder = () => {
    setIsOrderCloseOpen(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  useEffect(() => {
    if (orderId)
      dispatch(fetchOrderDetailsData({ orderId: parseInt(orderId) }));
  }, [orderId]);

  const isInSet = order?.item?.isinset || false;

  return (
    <Layout className="print:max-h-[148mm]">
      <div className={styles.OrderDetailsPage}>
        {/* Header */}
        <div className="w-full px-10 h-[200px] text-center flex flex-col justify-center relative">
          <FaArrowLeft
            onClick={handleGoToBack}
            className="absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
          />

          <h1 className="text-5xl mt-6 font-bold text-white">
            કામ ની વિગત
          </h1>
        </div>

        {/* Content */}
        <div className="w-full relative mt-[50px] px-4 py-4">
          {order ? (
            <div className="w-full">
              {/* Order Card */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Order ID Header */}
                <div className="bg-gradient-to-r from-primary-1 to-primary-2 px-4 py-3 flex justify-between items-center">
                  <span className="text-white text-lg font-bold">
                    ઓર્ડર #{order?.id}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    order?.status === 0
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {order?.status === 0 ? 'ચાલુ' : 'પૂર્ણ'}
                  </span>
                </div>

                {/* Image */}
                <div className="p-4">
                  <div className="w-full h-[180px] bg-gray-100 rounded-xl overflow-hidden">
                    {order?.image && (
                      <img
                        className="w-full h-full object-contain"
                        src={`${order.image}?order=${order.id}`}
                        alt=""
                      />
                    )}
                  </div>
                </div>

                {/* Order Details */}
                <div className="px-4 pb-4">
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    {/* Party */}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">પાર્ટી</span>
                      <span className="text-primary-1 font-bold text-right">
                        {order?.party?.firm_name || "-"}
                      </span>
                    </div>

                    {/* Item */}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">આઈટમ</span>
                      <span className="text-primary-1 font-bold flex items-center gap-2">
                        {order?.item?.name || "-"}
                        {isInSet && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            સેટ
                          </span>
                        )}
                      </span>
                    </div>

                    {/* Order Qty */}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">ઓર્ડર નંગ</span>
                      <span className="text-primary-1 font-bold">{order?.qty || 0}</span>
                    </div>

                    {/* Divider */}
                    {((order?.total_received_qty || 0) > 0 || (order?.total_received_weight || 0) > 0) && (
                      <div className="border-t border-gray-200 my-2"></div>
                    )}

                    {/* Received Summary for Regular Items or Set Item Final */}
                    {((order?.set_delivered_qty || 0) > 0 || (order?.set_delivered_weight || 0) > 0) && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">
                            {isInSet ? 'કેશ નંગ' : 'કુલ ડિલિવરી નંગ'}
                          </span>
                          <span className="text-primary-1 font-bold">{order?.set_delivered_qty || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">
                            {isInSet ? 'કેશ વજન' : 'કુલ ડિલિવરી વજન'}
                          </span>
                          <span className="text-primary-1 font-bold">
                            {(order?.set_delivered_weight || 0).toFixed(3)} કિલો
                          </span>
                        </div>
                      </>
                    )}

                    {/* Received Summary for Set Items */}
                    {isInSet && ((order?.delivered_qty || 0) > 0 || (order?.delivered_weight || 0) > 0) && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">પટ્ટા નંગ</span>
                          <span className="text-primary-1 font-bold">{order?.delivered_qty || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">પટ્ટા વજન</span>
                          <span className="text-primary-1 font-bold">
                            {(order?.delivered_weight || 0).toFixed(3)} કિલો
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-4">
                    <button
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-lg text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold shadow-md disabled:opacity-50"
                      type="button"
                      onClick={handlePrintOrderData}
                      disabled={!order?.id}
                    >
                      <FaFilePdf className="text-xl" />
                      PDF
                    </button>

                    {order?.status === 0 && !isOrderCloseOpen && (
                      <button
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-lg text-white bg-gradient-to-r from-green-500 to-green-600 font-bold shadow-md"
                        type="button"
                        onClick={handleCloseOrder}
                      >
                        <FaPlus className="text-xl" />
                        ડિલિવરી ઉમેરો
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Receive History */}
              {order?.receive_transactions && order.receive_transactions.length > 0 && (
                <ReceiveHistoryList
                  transactions={order.receive_transactions}
                  isInSet={isInSet}
                />
              )}

              {/* Receive Form */}
              {order?.status === 0 && isOrderCloseOpen && (
                <div ref={formRef} className="mt-4 bg-white rounded-2xl shadow-lg p-4">
                  <OrderCloseForm onSuccess={() => setIsOrderCloseOpen(false)} />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-10">
              <p className="text-primary-2 font-bold text-xl">
                હાલ કોઈ કામ ની વિગતો મળી નથી.
              </p>

              <Link to={route.newOrder}>
                <button
                  type="button"
                  className="mt-6 shadow rounded-2xl text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-8 py-3"
                >
                  કામ ઉમેરો
                </button>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Print Template */}
      <div ref={printRef} className="print:block hidden">
        <style>
          {`
            @media print {
              @page {
                size: 105mm 148mm;
                margin: 0;
              }
              html, body {
                width: 105mm;
                height: 148mm;
                margin: 0;
                padding: 0;
              }
            }
          `}
        </style>
        <OrderPdf order={order} />
      </div>
    </Layout>
  );
};

export default OrderDetailsPage;
